import { createSelector, createStructuredSelector } from 'reselect';

import { getHostId } from '../organization-select/getters';
import { getUserId, getRole, getUserMemberships, getAmplitudeInstance } from '../session/getters';

import { betaUserIds } from './betaUsers.js';

const keys = [
  'activity_report',
  'add_ons_report',
  'chargebacks_report',
  'cost_items_report',
  'events_report',
  'listings_report',
  'orders_report',
  'payout_settlement_report',
  'box_office_account_report',
  'settlement_report',
  'sales_by_event_report',
  'timeslot_changes_report',
  'waitlist_report',
  'attraction_report',
];

const textKey = {
  'payout_settlement_report': 'payout-settlement',
  'cost_items_report': 'tickets',
  'add_ons_report': 'addons',
  'box_office_account_report': 'box-office',
  'orders_report': ['orders', 'cancellations'],
  'activity_report': 'account-activity',
  'listings_report': 'ticket-type-event',
  'events_report': 'ticket-type-datetime',
  'chargebacks_report': 'chargebacks',
  'settlement_report': 'settlements',
  'sales_by_event_report': 'sales-by-event',
  'timeslot_changes_report': 'timeslot-changes',
  'waitlist_report': 'waitlist',
  'attraction_report': 'attraction'
};

const getCurrentOrganization = createSelector(
  [getUserMemberships, getHostId, getUserId],
  (memberships, hostId, userId) => {
    // viewing their own profile, no org.
    if (hostId === userId) {
      return null;
    }

    return memberships.find(membership => membership.organization.owner.id === hostId);
  }
);

const getHasPermissionForDiscountCodes = createSelector(
  [getCurrentOrganization],
  (currentOrganization) => currentOrganization ? currentOrganization.all_listings && currentOrganization.promote : true
);

const getHasPermissionForDashboard = createSelector(
  [getCurrentOrganization],
  (currentOrganization) => currentOrganization ? currentOrganization.manage_overview : true,
);

const getHasPermissionForManageAddOns = createSelector(
  [getCurrentOrganization],
  (currentOrganization) => currentOrganization ? currentOrganization.manage_add_ons : true,
);

const getHasPermissionForFees = createSelector(
  [getCurrentOrganization],
  (currentOrganization) => currentOrganization ? currentOrganization.manage_fee_presets : true,
);

export const getHasPermissionForReports = createSelector(
  [getHostId, getUserId, getCurrentOrganization],
  (hostId, userId, currentOrganization) => {
    if (currentOrganization && currentOrganization.report) {
      return (keys.some(k => currentOrganization[k] === true));
    }

    return hostId === userId;
  }
);

export const getShowNewDashboard = createSelector(
  [getHostId, getUserId, getRole],
  (hostId, userId, role) => {
    // TODO: Remove Ids once New Dashboard ships
    // Only respect admin role if the user is viewing their own profile.
    return (hostId === userId && role.toUpperCase() === 'ADMIN') || betaUserIds.includes(hostId);
  }
);

export const getDoesNotHavePermissionForReports = createSelector(
  [getCurrentOrganization],
  (currentOrganization) => {
    if (currentOrganization && currentOrganization.report) {
      const noPermission = keys.filter(k => currentOrganization[k] === false);
      const finalNoPerms = [];
      noPermission.forEach(permission => {
        if (permission in textKey){
          finalNoPerms.push(textKey[permission]);
        }
      });
      return [].concat(...finalNoPerms);
    }
    return [];
  }
);

export default createStructuredSelector({
  hasPermissionForReports: getHasPermissionForReports,
  hasPermissionForDiscountCodes: getHasPermissionForDiscountCodes,
  hasPermissionForDashboard: getHasPermissionForDashboard,
  hasPermissionForManageAddOns: getHasPermissionForManageAddOns,
  hasPermissionForFees: getHasPermissionForFees,
  amplitudeInstance: getAmplitudeInstance,
  doesNotHavePermissionForReports: getDoesNotHavePermissionForReports,
  showNewDashboard: getShowNewDashboard,
});



// WEBPACK FOOTER //
// ./src/modules/account-navbar/getters.js